/**
 * One line per tool call, the way Cursor shows them: a small icon, a short
 * verb-plus-target title ("Read I-129.pdf"), a chevron that appears on hover
 * and opens the call's arguments and result. While the call runs the icon is
 * a spinner and the title shimmers; an error turns the icon red; a call the
 * turn never got to (Stop) is a filled square.
 *
 * `StepRow` is the same line for a subagent's nested call (`data-step`),
 * indented under its `task` row.
 */
import type { LucideIcon } from "lucide-react";
import {
  AlertTriangle,
  ArrowUpRight,
  ChevronRight,
  CircleDashed,
  Loader2,
  PanelRight,
  Square
} from "lucide-react";
import { memo, useMemo, useState, type FC, type ReactNode } from "react";

import { Shimmer } from "@/components/ai-elements/shimmer";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from "@/components/ui/collapsible";
import type { StepData } from "@/features/agents/useAgentChat";
import { splitCitationTokens } from "@/features/draftCitations/model";
import { cn } from "@/lib/utils";

import type { ArtifactKind } from "../../types";
import { useTranscriptFileNames } from "../runEvents";
import { FALLBACK, useTitleFor } from "../useTitleFor";
import type { AnyToolPart } from "./ToolCard";
import {
  describeTool,
  errorRowTitle,
  friendlyToolError,
  hideRunToolInputOnError,
  isHarnessTool,
  showRawCallDetails,
  type ArtifactResolver,
  type ToolTitle
} from "./toolTitles";

export type RowStatus =
  | "running"
  | "done"
  | "error"
  | "stopped"
  | "pending"
  | "needs";

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === "object" && v !== null;

/**
 * The IDE's artifact names, as a resolver; a file the IDE does not know is
 * named from the transcript's earlier tool results; else it stays unnamed.
 */
export const useArtifactResolver = (): ArtifactResolver => {
  const titleFor = useTitleFor();
  const fileNames = useTranscriptFileNames();
  return useMemo<ArtifactResolver>(
    () => (kind: ArtifactKind, id: string) => {
      const title = titleFor({ kind, id });
      if (title !== FALLBACK[kind]) return title;
      return kind === "file" ? fileNames.get(id) ?? null : null;
    },
    [titleFor, fileNames]
  );
};

/** What a tool part reads as, resolved against the IDE's artifact names. */
export const useToolTitle = (
  name: string,
  input: unknown,
  output?: unknown
): ToolTitle => {
  const resolve = useArtifactResolver();
  return useMemo(
    () => describeTool(name, input, resolve, output),
    [name, input, resolve, output]
  );
};

/** A tool part's result, once it has one. */
export const outputOfPart = (part: AnyToolPart): unknown =>
  part.state === "output-available" ? part.output : undefined;

/** A draft the call queued behind its analyses has not been written yet. */
const queuedTitle = (
  name: string,
  part: AnyToolPart,
  title: ToolTitle
): string | null => {
  if (name !== "generate_draft" || part.state !== "output-available")
    return null;
  const draft =
    isRecord(part.output) && isRecord(part.output.draft)
      ? part.output.draft
      : null;
  const waiting = draft?.waiting_for;
  return Array.isArray(waiting) && waiting.length
    ? `Queued ${title.target ?? "a draft"}`
    : null;
};

/** Tools whose finished row reads the result's `message` (it knows the outcome, e.g. the exhibit label). */
const MESSAGE_TITLE_TOOLS: ReadonlySet<string> = new Set([
  "promote_to_case_files"
]);

const messageTitle = (name: string, part: AnyToolPart): string | null => {
  if (!MESSAGE_TITLE_TOOLS.has(name) || part.state !== "output-available")
    return null;
  const message = isRecord(part.output) ? part.output.message : null;
  return typeof message === "string" && message.trim() ? message : null;
};

/** File tools (`edit_file`, `write_file`) report failure as a string. */
const STRING_ERROR_RE = /^\s*Error\b:?\s*/;

const errorOfPart = (part: AnyToolPart): string | undefined => {
  if (part.state === "output-error") return part.errorText;
  if (part.state !== "output-available") return undefined;
  if (isRecord(part.output) && typeof part.output.error === "string")
    return part.output.error;
  if (typeof part.output === "string" && STRING_ERROR_RE.test(part.output))
    return part.output.replace(STRING_ERROR_RE, "") || part.output;
  return undefined;
};

/**
 * What a call refused for want of input (`{error, needs: ["email"]}`): the
 * agent asks for it next, so the row is not a failure.
 */
export const needsOfPart = (part: AnyToolPart): string[] => {
  if (part.state !== "output-available" || !isRecord(part.output)) return [];
  const { needs } = part.output;
  return Array.isArray(needs)
    ? needs.filter((n): n is string => typeof n === "string" && n !== "")
    : [];
};

const listInWords = (items: string[]): string =>
  items.length < 2
    ? items.join("")
    : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;

/** `["first_name", "email"]` → "Needs the client's first name and email". */
export const needsTitle = (needs: string[]): string =>
  `Needs the client's ${listInWords(needs.map((n) => n.replace(/_/g, " ")))}`;

/** The row status of a tool part. Approval requests are `pending`: the card below asks. */
export const rowStatusOf = (part: AnyToolPart): RowStatus => {
  const approval = "approval" in part ? part.approval : undefined;
  if (part.state === "approval-requested") return "pending";
  if (needsOfPart(part).length) return "needs";
  if (errorOfPart(part)) return "error";
  if (part.state === "output-available") return "done";
  if (part.state === "output-denied") return approval ? "done" : "stopped";
  return "running";
};

/** Citation markers as the label they print as; a bare node id means nothing to a reader. */
const readableCitations = (text: string): string =>
  splitCitationTokens(text)
    .map((t) => (t.kind === "text" ? t.text : `[${t.label ?? "citation"}]`))
    .join("");

const pretty = (value: unknown): string => {
  if (value === undefined) return "";
  if (typeof value === "string") return readableCitations(value);
  if (isRecord(value) && Object.keys(value).length === 0) return "";
  try {
    return readableCitations(JSON.stringify(value, null, 2));
  } catch {
    return String(value);
  }
};

const hasCallDetails = (
  input: unknown,
  output: unknown,
  error?: string
): boolean => Boolean(pretty(input) || pretty(output) || error);

/** The call's arguments and result, in a quiet mono pane. */
export const CallDetails: FC<{
  input?: unknown;
  output?: unknown;
  error?: string;
  /** When set, the error reads as prose instead of a mono debug block. */
  plainError?: boolean;
  className?: string;
}> = ({ input, output, error, plainError = false, className }) => {
  const args = pretty(input);
  const result = pretty(output);
  if (!args && !result && !error) return null;
  return (
    <div
      className={cn("space-y-1.5 text-xs text-fg-3", className)}
      data-testid="call-details"
    >
      {args ? (
        <pre className="max-h-40 overflow-auto rounded-md bg-fill-1 p-2 font-mono leading-snug whitespace-pre-wrap break-words">
          {args}
        </pre>
      ) : null}
      {error ? (
        plainError ? (
          <p
            className="text-sm leading-snug text-destructive"
            data-testid="call-details-error"
          >
            {error}
          </p>
        ) : (
          <pre className="max-h-40 overflow-auto rounded-md bg-danger-soft p-2 font-mono leading-snug whitespace-pre-wrap break-words text-destructive">
            {error}
          </pre>
        )
      ) : result ? (
        <pre className="max-h-48 overflow-auto rounded-md bg-fill-1 p-2 font-mono leading-snug whitespace-pre-wrap break-words">
          {result}
        </pre>
      ) : null}
    </div>
  );
};

type LineProps = {
  icon: LucideIcon;
  status: RowStatus;
  title: string;
  /** Text after the title, quieter (a count, "stopped"). */
  suffix?: string | null;
  /** Controls at the end of the line, outside the fold's trigger (Undo). */
  trailing?: ReactNode;
  /** Strike the title through (an undone write). */
  struck?: boolean;
  /** Expandable detail: arguments/result, or nested step rows. */
  children?: ReactNode;
  defaultOpen?: boolean;
  /** Keep the detail open (live steps under a running subagent). */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  className?: string;
  testId?: string;
  /** Extra `data-*` attributes for tests and styling hooks. */
  data?: Record<string, string | undefined>;
  /** Makes the title a link to what the call made or acted on; the chevron alone folds the detail. */
  onOpenTarget?: () => void;
  /** The link previews in the artifact panel rather than leaving the page. */
  targetInPanel?: boolean;
};

/** The line itself: icon, title, chevron; the detail folds under it. */
export const ActivityLine: FC<LineProps> = ({
  icon: Icon,
  status,
  title,
  suffix,
  trailing,
  struck = false,
  children,
  defaultOpen = false,
  open,
  onOpenChange,
  className,
  testId = "tool-row",
  data,
  onOpenTarget,
  targetInPanel = false
}) => {
  const [innerOpen, setInnerOpen] = useState(defaultOpen);
  const isOpen = open ?? innerOpen;
  const setOpen = onOpenChange ?? setInnerOpen;
  const expandable = children != null && children !== false;
  const running = status === "running";
  const statusIcon = (
    <span className="flex size-3.5 shrink-0 items-center justify-center">
      {running ? (
        <Loader2 className="size-3.5 animate-spin text-fg-3" />
      ) : status === "error" ? (
        <AlertTriangle className="size-3.5 text-destructive" />
      ) : status === "needs" ? (
        <CircleDashed
          className="size-3.5 text-fg-3"
          data-testid="tool-row-needs"
        />
      ) : status === "stopped" ? (
        <Square
          className="size-2.5 fill-current text-fg-3"
          data-testid="tool-row-stopped"
        />
      ) : (
        <Icon className="size-3.5 text-fg-3" />
      )}
    </span>
  );
  const titleText = (
    <>
      {running ? (
        <Shimmer as="span" className="text-base" duration={1.6}>
          {title}
        </Shimmer>
      ) : (
        <span
          className={cn(
            status === "error" && "text-destructive",
            struck && "line-through"
          )}
        >
          {title}
        </span>
      )}
      {suffix ? <span className="text-fg-3">{` ${suffix}`}</span> : null}
    </>
  );
  const chevron = expandable ? (
    <ChevronRight
      className={cn(
        "size-3 shrink-0 text-fg-4 opacity-0 transition-[transform,opacity] group-hover/row:opacity-100 group-data-[state=open]/row:opacity-100",
        isOpen && "rotate-90"
      )}
    />
  ) : null;
  const TargetIcon = targetInPanel ? PanelRight : ArrowUpRight;
  const trigger = onOpenTarget ? (
    <div
      className={cn(
        "-ml-1 flex h-6 min-w-0 items-center gap-1.5 px-1 text-base text-fg-3",
        trailing ? "flex-1" : "w-[calc(100%+0.25rem)]"
      )}
      data-testid={`${testId}-trigger`}
    >
      {statusIcon}
      <button
        type="button"
        onClick={onOpenTarget}
        className="group/target flex min-w-0 items-center gap-1 rounded-sm text-left hover:text-fg focus-visible:text-fg focus-visible:outline-none"
        data-testid={`${testId}-open`}
      >
        <span className="min-w-0 truncate underline-offset-2 group-hover/target:underline group-focus-visible/target:underline">
          {titleText}
        </span>
        <TargetIcon className="size-3 shrink-0 text-fg-4" aria-hidden />
      </button>
      <span className="flex-1" />
      {expandable ? (
        <CollapsibleTrigger
          aria-label="Show details"
          className="flex size-5 shrink-0 items-center justify-center rounded-sm hover:bg-fill-1 hover:text-fg"
          data-testid={`${testId}-details`}
        >
          {chevron}
        </CollapsibleTrigger>
      ) : null}
    </div>
  ) : (
    <CollapsibleTrigger
      disabled={!expandable}
      className={cn(
        "-ml-1 flex h-6 min-w-0 items-center gap-1.5 rounded-sm px-1 text-left text-base text-fg-3",
        trailing ? "flex-1" : "w-[calc(100%+0.25rem)]",
        expandable && "hover:bg-fill-1 hover:text-fg",
        !expandable && "cursor-default"
      )}
      data-testid={`${testId}-trigger`}
    >
      {statusIcon}
      <span className="min-w-0 flex-1 truncate">{titleText}</span>
      {chevron}
    </CollapsibleTrigger>
  );
  return (
    <Collapsible
      open={expandable ? isOpen : false}
      onOpenChange={setOpen}
      className={cn("group/row min-w-0", className)}
      data-testid={testId}
      data-status={status}
      {...Object.fromEntries(
        Object.entries(data ?? {}).map(([k, v]) => [`data-${k}`, v])
      )}
    >
      {trailing ? (
        <div className="flex min-w-0 items-center gap-1">
          {trigger}
          <div className="flex shrink-0 items-center gap-1">{trailing}</div>
        </div>
      ) : (
        trigger
      )}
      {expandable ? (
        <CollapsibleContent className="pt-1 pb-1 pl-6">
          {children}
        </CollapsibleContent>
      ) : null}
    </Collapsible>
  );
};

type ToolRowProps = {
  part: AnyToolPart;
  /** Rendered inside the fold instead of the call's arguments and result. */
  detail?: ReactNode;
  /** Text after the title (a result count). */
  suffix?: string | null;
  /** Controls at the end of the line (the write's Undo). */
  trailing?: ReactNode;
  /** Strike the title through (an undone write). */
  struck?: boolean;
  defaultOpen?: boolean;
  className?: string;
  /** Opens the one entity the call returned; the title becomes its link. */
  onOpenTarget?: () => void;
  targetInPanel?: boolean;
  /** The finished title when the result changes it ("Listed 12 clients"). */
  doneTitle?: string | null;
};

/** A top-level tool call as one line. */
export const ToolRow: FC<ToolRowProps> = memo(
  ({
    part,
    detail,
    suffix,
    trailing,
    struck,
    defaultOpen,
    className,
    onOpenTarget,
    targetInPanel,
    doneTitle
  }) => {
    const name =
      part.type === "dynamic-tool"
        ? part.toolName
        : part.type.replace(/^tool-/, "");
    const output = outputOfPart(part);
    const title = useToolTitle(name, part.input, output);
    const status = rowStatusOf(part);
    const needs = status === "needs";
    const done = status === "done" || status === "error" || needs;
    const error = needs ? undefined : errorOfPart(part);
    const stopped = status === "stopped";
    const declined = "approval" in part && part.approval?.approved === false;
    const runError =
      status === "error" && title.kind === "run" && Boolean(error);
    const hideInputOnError = runError && hideRunToolInputOnError(name);
    const detailError = error
      ? runError
        ? friendlyToolError(name, error)
        : error
      : undefined;
    return (
      <ActivityLine
        icon={title.icon}
        status={status}
        title={
          needs
            ? needsTitle(needsOfPart(part))
            : status === "error"
            ? errorRowTitle(name, title)
            : status === "done" && !declined
            ? queuedTitle(name, part, title) ??
              messageTitle(name, part) ??
              doneTitle ??
              title.past
            : title.present
        }
        suffix={stopped ? "stopped" : suffix}
        trailing={trailing}
        struck={struck}
        defaultOpen={defaultOpen}
        className={className}
        data={{ tool: name, "state-tool": part.state, kind: title.kind }}
        onOpenTarget={onOpenTarget}
        targetInPanel={targetInPanel}
      >
        {detail ??
          (showRawCallDetails(name) &&
          (part.input !== undefined || done) &&
          hasCallDetails(
            hideInputOnError ? undefined : part.input,
            hideInputOnError ? undefined : output,
            detailError
          ) ? (
            <CallDetails
              input={hideInputOnError ? undefined : part.input}
              output={hideInputOnError ? undefined : output}
              error={detailError}
              plainError={runError}
            />
          ) : null)}
      </ActivityLine>
    );
  }
);
ToolRow.displayName = "ToolRow";

/** A subagent's nested call (`data-step`) as one line, under its task row. */
export const StepRow: FC<{ step: StepData }> = memo(({ step }) => {
  const name = step.name ?? "tool";
  const title = useToolTitle(
    name,
    step.input,
    step.state === "output-available" ? step.output : undefined
  );
  const status: RowStatus =
    step.state === "output-error"
      ? "error"
      : step.state === "output-available"
      ? "done"
      : "running";
  const done = status !== "running";
  return (
    <ActivityLine
      icon={title.icon}
      status={status}
      title={status === "done" ? title.past : title.present}
      testId="step-row"
      data={{ tool: name, step: step.callId }}
    >
      {done &&
      showRawCallDetails(name) &&
      hasCallDetails(step.input, step.output, step.error) ? (
        <CallDetails
          input={step.input}
          output={step.output}
          error={step.error}
        />
      ) : null}
    </ActivityLine>
  );
});
StepRow.displayName = "StepRow";

/** The live steps of a `task` subagent, as rows under its line. */
export const StepRows: FC<{
  steps: readonly StepData[];
  className?: string;
}> = ({ steps, className }) => (
  <div
    className={cn(
      "flex flex-col gap-0.5 border-l border-line-2 pl-2",
      className
    )}
    data-testid="step-rows"
  >
    {steps
      .filter((step) => !isHarnessTool(step.name ?? ""))
      .map((step) => (
        <StepRow key={step.callId} step={step} />
      ))}
  </div>
);
